import React, { useCallback, useEffect, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { Login } from './components/Login';
import { LandingPage } from './pages/LandingPage';
import { Profile } from './pages/Profile';
import { Users, UserRole } from './pages/Users';
import { Regelgrundlag } from './pages/Regelgrundlag';
import { MesterAI } from './pages/MesterAI';
import { QualityAssurance } from './pages/QualityAssurance';
import { QualityReportDetail } from './pages/QualityReportDetail';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { BottomNav } from './components/BottomNav';
import { ProWorkspace } from './pages/pro/ProWorkspace';
import { useFirestoreCollection } from './hooks/useFirestore';
import { useProWorkspace } from './hooks/useProWorkspace';
import { View } from './types/view';
import { QualityReport } from './types/quality';
import { qualityReports as initialQualityReports } from './data/qualityReports';
import { company } from './data/company';
import { SearchResult } from './utils/search';
type Selection = {
  kind: 'customer' | 'job' | 'quote' | 'lead';
  id: string;
} | null;
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState<View>('landing');
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState<UserRole>('Medarbejder');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [selection, setSelection] = useState<Selection>(null);
  const [selectedQualityReportId, setSelectedQualityReportId] = useState<string | null>(null);
  const hasSeededQuality = useRef(false);
  const pro = useProWorkspace();
  const {
    data: qualityReports,
    loading: qualityLoading,
    add: addQualityReport,
    update: updateQualityReport,
    remove: removeQualityReport
  } = useFirestoreCollection<QualityReport>('qualityReports');

  // Seed the example reports once, so the section is not empty on first visit.
  useEffect(() => {
    if (qualityLoading || hasSeededQuality.current) return;
    hasSeededQuality.current = true;
    if (qualityReports.length === 0) {
      initialQualityReports.forEach(({
        id,
        ...report
      }) => {
        addQualityReport(report as Omit<QualityReport, 'id'>).catch(() => {
          /* seeding is best effort */
        });
      });
      return;
    }
    qualityReports.filter(report => report.company?.name !== company.name).forEach(report => {
      if (!report.id) return;
      updateQualityReport(report.id, {
        company: {
          ...company
        }
      } as Partial<QualityReport>).catch(() => {
        /* best effort */
      });
    });
  }, [qualityLoading, qualityReports, addQualityReport, updateQualityReport]);

  // Restore an existing session.
  useEffect(() => {
    const checkAuth = async () => {
      const authStatus = localStorage.getItem('isAuthenticated');
      const storedUsername = localStorage.getItem('username');
      if (authStatus !== 'true' || !storedUsername) return;
      setIsAuthenticated(true);
      setUsername(storedUsername);
      setCurrentView('pro-dashboard');
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const users: any[] = [];
        snapshot.forEach(entry => users.push({
          id: entry.id,
          ...entry.data()
        }));
        const user = users.find(u => u.email === storedUsername);
        if (user) setUserRole(user.role);
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };
    checkAuth();
  }, []);
  const handleLogin = (user: string, role: UserRole) => {
    setUsername(user);
    setUserRole(role);
    setIsAuthenticated(true);
    setCurrentView('pro-dashboard');
  };
  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setCurrentView('landing');
  };
  const handleChangePassword = async (currentPassword: string, newPassword: string): Promise<boolean> => {
    if (currentPassword !== localStorage.getItem('password')) return false;
    localStorage.setItem('password', newPassword);
    return true;
  };
  const isAdmin = userRole === 'Admin';
  const isReadOnly = userRole === 'Read-Only';
  const selectedQualityReport = selectedQualityReportId ? qualityReports.find(r => r.id === selectedQualityReportId) : null;
  const handleOpenQualityReport = (reportId: string) => {
    setSelectedQualityReportId(reportId);
    setCurrentView('quality-detail');
  };
  const handleSaveQualityReport = async (updated: QualityReport) => {
    try {
      if (!updated.id) return;
      const {
        id,
        ...fields
      } = updated;
      await updateQualityReport(id, fields as Partial<QualityReport>);
    } catch (error) {
      console.error('Error saving quality report:', error);
      alert('Fejl ved gemning af kvalitetssikring.');
    }
  };
  const getNextQualityReportId = () => {
    const year = new Date().getFullYear();
    const numbers = qualityReports.map(r => parseInt(r.reportId?.split('-').pop() || '0', 10)).filter(n => !Number.isNaN(n));
    const next = numbers.length === 0 ? 1 : Math.max(...numbers) + 1;
    return `KS-${year}-${next.toString().padStart(4, '0')}`;
  };
  const handleCreateQualityReport = async (report: Omit<QualityReport, 'id'>) => {
    try {
      const newId = await addQualityReport(report);
      if (newId) handleOpenQualityReport(newId);
    } catch (error) {
      console.error('Error creating quality report:', error);
      alert('Fejl ved oprettelse af kvalitetssikring.');
    }
  };
  const handleDeleteQualityReport = async (report: QualityReport) => {
    if (!window.confirm(`Er du sikker på at du vil slette kvalitetssikringen "${report.title}"?`)) return;
    try {
      if (report.id) await removeQualityReport(report.id);
    } catch (error) {
      console.error('Error deleting quality report:', error);
      alert('Fejl ved sletning af kvalitetssikring.');
    }
  };

  /** Global search jumps straight to the matched record. */
  const handleSearchResult = (result: SearchResult) => {
    if (result.view === 'quality-detail' && result.recordId) {
      handleOpenQualityReport(result.recordId);
      return;
    }
    if (result.recordId) {
      const kind = result.kind === 'customer' ? 'customer' : result.kind === 'job' ? 'job' : 'quote';
      setSelection({
        kind,
        id: result.recordId
      });
    }
    setCurrentView(result.view);
  };
  const clearSelection = useCallback(() => setSelection(null), []);
  if (!isAuthenticated && currentView === 'landing') {
    return <LandingPage onLoginClick={() => setCurrentView('login')} data-id="element-0" data-mp="2phoklyxika1p" />;
  }
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} data-id="element-1" data-mp="33hfqfcdvmk4m" />;
  }
  return <div className="min-h-screen w-full bg-stone-50 lg:flex" data-id="element-2" data-mp="2yi3731m7rv6s">
      <Sidebar currentView={currentView} onNavigate={view => {
      setCurrentView(view);
      setIsMobileNavOpen(false);
    }} isAdmin={isAdmin} username={username} userRole={userRole} onLogout={handleLogout} isMobileOpen={isMobileNavOpen} onMobileClose={() => setIsMobileNavOpen(false)} data-id="element-3" data-mp="2yfqfdwqnm6gj" />

      <div className="min-w-0 flex-1" data-id="element-4" data-mp="3d38qpdyq1zbx">
        <TopBar customers={pro.customers.data} jobs={pro.jobs.data} quotes={pro.quotes.data} qualityReports={qualityReports} isAdmin={isAdmin} onSelectResult={handleSearchResult} data-id="element-5" data-mp="2zd576ur88jq6" />

        <main className="mx-auto max-w-[1600px] px-4 pb-28 pt-6 sm:px-6 sm:pt-8 lg:px-10 lg:pb-8" data-id="element-6" data-mp="2pga9ge2s2ncy">
          <ProWorkspace view={currentView} onNavigate={setCurrentView} data={pro} selection={selection} onSelectionHandled={clearSelection} data-id="element-7" data-mp="2uetwkht3y56w" />

          {currentView === 'mester-ai' && <MesterAI materials={pro.materials.data} defaultHourlyRate={pro.settings.hourlyRate} data-id="element-8" data-mp="30v3tajqkgnxu" />}
          {currentView === 'quality' && <QualityAssurance reports={qualityReports} customers={pro.customers.data} onOpenReport={handleOpenQualityReport} onCreateReport={handleCreateQualityReport} onDeleteReport={handleDeleteQualityReport} nextReportId={getNextQualityReportId()} responsible={{
          name: username,
          email: username,
          phone: ''
        }} canEdit={!isReadOnly} data-id="element-9" data-mp="2qx7i5rxmpszq" />}
          {currentView === 'quality-detail' && selectedQualityReport && <QualityReportDetail report={selectedQualityReport} onBack={() => {
          setSelectedQualityReportId(null);
          setCurrentView('quality');
        }} onChange={handleSaveQualityReport} data-id="element-10" data-mp="238y0n27g7ree" />}
          {currentView === 'regelgrundlag' && <Regelgrundlag data-id="element-11" data-mp="9qz44vg1fzqq" />}
          {currentView === 'profile' && <Profile username={username} onChangePassword={handleChangePassword} data-id="element-12" data-mp="dp6jbluusszt" />}
          {currentView === 'users' && isAdmin && <Users currentUserEmail={username} data-id="element-13" data-mp="nnmbls5ma8aw" />}
        </main>
      </div>

      <BottomNav currentView={currentView} onNavigate={view => {
      setIsMobileNavOpen(false);
      setCurrentView(view);
    }} onOpenMore={() => setIsMobileNavOpen(!isMobileNavOpen)} isMoreOpen={isMobileNavOpen} data-id="element-14" data-mp="3m72p2iiijtkj" />
    </div>;
}